import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { bulkUpdateAttendees, exportCsvUrl } from "@/lib/attendeesApi";

const CORE_COLUMNS = [
  { key: "email", label: "Email" },
  { key: "name", label: "Name" },
  { key: "mobile", label: "Mobile" },
  { key: "company", label: "Company" },
  { key: "role", label: "Role" },
  { key: "ticket", label: "Ticket" },
  { key: "country", label: "Country" },
  { key: "status", label: "Status" },
];

function cellValue(row, key) {
  if (key in (row.customAnswers || {})) {
    const v = row.customAnswers[key];
    return typeof v === "object" ? JSON.stringify(v) : String(v ?? "");
  }
  return String(row[key] ?? "");
}

export default function AttendeeSpreadsheet({
  eventId,
  mode,
  open,
  onClose,
  rows,
  selectedIds,
  customFieldKeys,
  onSaved,
}) {
  const [visibleCols, setVisibleCols] = useState([]);
  const [grid, setGrid] = useState([]);
  const [busy, setBusy] = useState(false);

  const allColumns = useMemo(() => {
    const custom = (customFieldKeys || []).map((k) => ({ key: k, label: k, custom: true }));
    return [...CORE_COLUMNS, ...custom];
  }, [customFieldKeys]);

  const workingRows = useMemo(() => {
    if (mode === "export" && selectedIds?.length) {
      return rows.filter((r) => selectedIds.includes(r.id));
    }
    if (mode === "bulk" && selectedIds?.length) {
      return rows.filter((r) => selectedIds.includes(r.id));
    }
    if (mode === "export") return rows;
    return rows.filter((r) => selectedIds?.includes(r.id));
  }, [rows, selectedIds, mode]);

  useEffect(() => {
    if (!open) return;
    setVisibleCols(allColumns.map((c) => c.key));
    setGrid(
      workingRows.map((r) => ({
        id: r.id,
        cells: Object.fromEntries(allColumns.map((c) => [c.key, cellValue(r, c.key)])),
      })),
    );
  }, [open, workingRows, allColumns]);

  const toggleCol = (key) => {
    setVisibleCols((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const updateCell = (rowId, key, value) => {
    setGrid((g) =>
      g.map((row) => (row.id === rowId ? { ...row, cells: { ...row.cells, [key]: value } } : row)),
    );
  };

  const saveBulk = async () => {
    setBusy(true);
    try {
      const coreKeys = new Set(CORE_COLUMNS.map((c) => c.key));
      const updates = grid.map((row) => {
        const patch = {};
        const custom_answers = {};
        for (const [k, v] of Object.entries(row.cells)) {
          if (!visibleCols.includes(k)) continue;
          if (coreKeys.has(k)) patch[k] = v;
          else custom_answers[k] = v;
        }
        if (Object.keys(custom_answers).length) patch.custom_answers = custom_answers;
        return { id: row.id, patch };
      });
      const result = await bulkUpdateAttendees(eventId, updates);
      toast.success(`Updated ${result.updated ?? 0} attendee(s)`);
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error("Bulk edit failed", { description: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  const downloadExport = () => {
    const ids = workingRows.map((r) => r.id).filter(Boolean);
    const url = exportCsvUrl(eventId, {
      ids: mode === "export" && selectedIds?.length ? ids : undefined,
      columns: visibleCols,
    });
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Export started");
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/70" role="dialog" aria-modal="true" aria-label={mode === "bulk" ? "Bulk edit" : "Export"}>
      <div className="flex-1 flex flex-col m-4 md:m-8 surface border border-[var(--border-default)] min-h-0">
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-default)] shrink-0">
          <div>
            <div className="text-overline">{mode === "bulk" ? "Bulk edit" : "Export CSV"}</div>
            <h2 className="font-heading text-xl mt-1">
              {workingRows.length.toLocaleString()} row{workingRows.length === 1 ? "" : "s"}
            </h2>
          </div>
          <button type="button" className="btn-ghost p-2" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-3 border-b border-[var(--border-default)] flex flex-wrap gap-2 shrink-0">
          <span className="text-overline w-full">Columns</span>
          {allColumns.map((c) => (
            <label key={c.key} className="text-xs font-mono flex items-center gap-1">
              <input
                type="checkbox"
                className="accent-[var(--primary)]"
                checked={visibleCols.includes(c.key)}
                onChange={() => toggleCol(c.key)}
              />
              {c.label}
            </label>
          ))}
        </div>

        <div className="flex-1 overflow-auto min-h-0" role="grid">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-[var(--bg-elev)] z-10">
              <tr>
                {visibleCols.map((k) => {
                  const col = allColumns.find((c) => c.key === k);
                  return (
                    <th key={k} className="p-2 border border-[var(--border-default)] text-left text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)] min-w-[120px]">
                      {col?.label || k}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {grid.map((row) => (
                <tr key={row.id}>
                  {visibleCols.map((k) => (
                    <td key={`${row.id}-${k}`} className="p-0 border border-[var(--border-default)]">
                      {mode === "bulk" ? (
                        <input
                          className="w-full min-w-[120px] bg-transparent px-2 py-1.5 text-xs focus:outline-none focus:bg-[var(--bg-elev)]"
                          value={row.cells[k] ?? ""}
                          onChange={(ev) => updateCell(row.id, k, ev.target.value)}
                        />
                      ) : (
                        <span className="block px-2 py-1.5 text-xs font-mono text-[var(--text-secondary)]">{row.cells[k] ?? ""}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-[var(--border-default)] flex justify-end gap-2 shrink-0">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          {mode === "bulk" ? (
            <button type="button" className="btn-primary" disabled={busy || !grid.length} onClick={saveBulk}>
              {busy ? "Saving…" : "Save changes"}
            </button>
          ) : (
            <button type="button" className="btn-primary" disabled={!visibleCols.length} onClick={downloadExport}>
              Download CSV
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
